/** @format */

/**
 * 会话分析 — 单会话详情：描述信息 + RootSpans 时间轴（GetAppMonitorSessionDetail）。
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { _$, Card, Breadcrumb, BreadcrumbItem } from '@king-design/react'
import { CustomPageContainer, CopyButton } from 'src/components'
import { useLocation, useHistory } from 'react-router'
import { Descriptions, DescriptionItem } from 'src/components'
import { useAsync } from 'react-use'
import Timeline from './Timeline'
import SpanItem from './SpanItem'
import type { SessionDetail } from './typescript'
import { formatDetailTime } from 'src/utils'
import { getSessionDetail } from 'src/api/ApiAIObservability'
import { durationFormatCompact, isRouteContainsPath } from 'src/utils/common'
import { tokenValueFormat } from '../../constant'
import useNavRegion from 'src/hooks/useNavRegion'
import variable from 'src/static/styles/var'

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const SessionDetailPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const serviceName = query.get('serviceName')
  const sessionId = query.get('sessionId')
  const from = query.get('from')
  const isFromService = isRouteContainsPath('/monitor/aiobservability/servicedetail')

  const [expand, setExpand] = useState<{ [key: string]: boolean }>({})
  const [activeTraceId, setActiveTraceId] = useState<string>()
  const traceListRef = useRef<HTMLDivElement>(null)

  // 控制台region
  const region = useNavRegion()

  /** 时间轴点击：高亮对应链路并滚动到 SpanItem */
  const scrollToTraceItem = useCallback((traceId: string) => {
    if (!traceId) {
      return
    }
    setActiveTraceId(prev => (prev === traceId ? undefined : traceId))
    setExpand(prev => {
      if (prev[traceId]) {
        return prev
      }
      return { ...prev, [traceId]: true }
    })
    requestAnimationFrame(() => {
      const el = traceListRef.current?.querySelector<HTMLElement>(`[data-trace-id="${traceId}"]`)
      el?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
  }, [])

  const toggleSpanExpand = useCallback((traceId: string) => {
    setExpand(prev => ({
      ...prev,
      [traceId]: !prev[traceId]
    }))
  }, [])

  const { value: sessionDetail } = useAsync(async (): Promise<SessionDetail | undefined> => {
    if (!serviceName || !sessionId) {
      return undefined
    }
    const result = await getSessionDetail({
      ServiceName: serviceName,
      SessionId: sessionId
    })
    return result?.SessionId ? (result as SessionDetail) : undefined
  }, [serviceName, sessionId, region])

  useEffect(() => {
    if (!sessionDetail?.RootSpans?.length) {
      return
    }
    const expandMap = sessionDetail.RootSpans.reduce((acc, trace, index) => {
      acc[trace.TraceId] = index === 0 ? true : false
      return acc
    }, {} as { [key: string]: boolean })
    setExpand(expandMap)
  }, [sessionDetail?.SessionId])

  // 跳转Trace详情（携带 from 供 Trace 详情页显式返回，避免 goBack 受历史栈污染影响）
  const toTraceDetail = (row: { TraceId: string; StartTime: string }) => {
    const qs = new URLSearchParams({
      from: 'session',
      traceId: row.TraceId,
      sessionId: sessionId || ''
    })
    const hashPath = isFromService
      ? `/monitor/aiobservability/servicedetail/traceanDetail?${qs.toString()}`
      : `/monitor/aiobservability/traceanalysis/detail?${qs.toString()}`
    history.push(hashPath)
  }

  const errorCount = Number(sessionDetail?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  const renderPageTitle = useMemo(() => {
    return isFromService ? (
      <Breadcrumb>
        <BreadcrumbItem to="/monitor/aiobservability/serviceoverview">
          {_$('服务总览')}
        </BreadcrumbItem>
        <BreadcrumbItem
          to={`/monitor/aiobservability/servicedetail?serviceName=${serviceName}&tab=session`}
        >
          {_$('服务详情')}
        </BreadcrumbItem>
        <BreadcrumbItem>{sessionId ?? '-'}</BreadcrumbItem>
      </Breadcrumb>
    ) : (
      sessionId ?? '-'
    )
  }, [from, sessionId, isFromService])

  return (
    <div className={className}>
      <CustomPageContainer title={renderPageTitle} showBack={from !== 'service' && !isFromService}>
        <Card type="none" title={_$('基本信息')} className="session-detail-desc">
          <Descriptions columns={3}>
            <DescriptionItem label={_$('会话ID')}>
              <div className="ksc-space">
                <span style={{ marginRight: 4 }}>{sessionDetail?.SessionId ?? '-'}</span>
                <CopyButton text={sessionDetail?.SessionId ?? '-'} />
              </div>
            </DescriptionItem>
            <DescriptionItem label={_$('服务名称')}>
              {sessionDetail?.ServiceName ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('耗时')}>
              {sessionDetail?.DurationMs != null
                ? durationFormatCompact(sessionDetail.DurationMs)
                : '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('会话开始时间')}>
              {formatDetailTime(sessionDetail?.StartTime)}
            </DescriptionItem>
            <DescriptionItem label={_$('会话结束时间')}>
              {formatDetailTime(sessionDetail?.EndTime)}
            </DescriptionItem>
            <DescriptionItem label={_$('LLM调用次数')}>
              {sessionDetail?.InvocationCount ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('错误次数')}>
              <span className={hasErrorCount ? 'session-detail-error-count' : undefined}>
                {sessionDetail?.ErrorCount ?? '-'}
              </span>
            </DescriptionItem>
            <DescriptionItem label={_$('链路数量')}>
              {sessionDetail?.TraceCount ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('TotalTokens')}>
              {tokenValueFormat(sessionDetail?.TotalTokens)}
            </DescriptionItem>
            <DescriptionItem label={_$('输入Token')}>
              {tokenValueFormat(sessionDetail?.InputTokens)}
            </DescriptionItem>
            <DescriptionItem label={_$('输出Token')}>
              {tokenValueFormat(sessionDetail?.OutputTokens)}
            </DescriptionItem>
            <DescriptionItem label={_$('缓存读取Token')}>
              {tokenValueFormat(sessionDetail?.CacheReadTokens)}
            </DescriptionItem>
          </Descriptions>
        </Card>
        <Card type="none" className="session-detail-timeline">
          <Timeline
            spans={sessionDetail?.RootSpans ?? []}
            toTraceDetail={toTraceDetail}
            onTraceClick={scrollToTraceItem}
          />
        </Card>
        {sessionDetail?.SessionTraces?.length ? (
          <Card type="none" className="session-detail-trace-list">
            <div ref={traceListRef}>
              {sessionDetail?.SessionTraces.map((trace, index) => (
                <SpanItem
                  index={index}
                  key={trace.TraceId}
                  toTraceDetail={toTraceDetail}
                  trace={trace}
                  expand={expand[trace.TraceId]}
                  active={activeTraceId === trace.TraceId}
                  onExpandChange={() => toggleSpanExpand(trace.TraceId)}
                />
              ))}
            </div>
          </Card>
        ) : null}
      </CustomPageContainer>
    </div>
  )
}

export default styled(SessionDetailPage)`
  .session-detail-desc {
    max-width: 100%;
    box-sizing: border-box;

    .session-detail-error-count {
      color: ${variable.errorColor};
    }
  }

  .session-detail-timeline {
    margin-top: 16px;
  }
  .session-detail-trace-list {
    margin-top: 16px;
  }
`
